/**
 * Request Board — the columns, the people directory, ranks and the small
 * helpers the board's screens share. Cards, history and alert rules live on the
 * backend (`/request_board`, see src/api/requestBoardApi.js); nothing here is
 * sample data any more.
 *
 * The process the board encodes:
 *   - Cards are filed into Requests: Urgent cards are pinned on top and worked
 *     first, then the Queue in rank order. Each card can be tagged with what it
 *     needs (Fix / Change / Question, any mix, or none yet).
 *   - Cards move left to right: to Requires Backend Changes (with a note of what
 *     the backend needs) and/or Frontend In Progress.
 *   - Finished work — or an answered question — goes to Completed - Awaiting
 *     Review, then is approved (→ Approved) or sent back with a comment.
 * There are no roles: anyone who can open the board can make any change.
 */
import { amber, slate, status, steel, teal } from "../../theme/colors";
import { timestampMs } from "../../_src_shared/utils/functions/formatDateTime";

/**
 * The people directory, keyed by EMAIL: { key, name, initials, color }. Filled
 * from `GET /request_board/meta` (registerPeople) before the board renders, and
 * read by every avatar / name on the board. Unknown emails fall back to the
 * email itself (see personFor).
 */
export const PEOPLE = {};

const AVATAR_COLORS = [teal[600], steel.main, amber.dark, slate[600], status.success.fg, slate[700]];

const colorFor = (email) => {
  let h = 0;
  for (let i = 0; i < email.length; i += 1) h = (h * 31 + email.charCodeAt(i)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const initialsOf = (email) => {
  const words = String(email || "").split("@")[0].split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] || "") + (words[1]?.[0] || "")).toUpperCase() || "?";
};

/** A directory entry for one email — registered or not. */
export const personFor = (email) => {
  const key = String(email || "").toLowerCase();
  if (!key) return null;
  if (!PEOPLE[key]) PEOPLE[key] = { key, name: key, initials: initialsOf(key), color: colorFor(key) };
  return PEOPLE[key];
};

/** Load `meta.people` (and `meta.me`) into the directory. */
export const registerPeople = (people = []) => {
  people.forEach((p) => {
    if (!p?.email) return;
    const key = p.email.toLowerCase();
    PEOPLE[key] = {
      key,
      name: p.name || key,
      initials: p.initials || initialsOf(key),
      color: colorFor(key),
    };
  });
};

/**
 * The people a comment @mentions: anyone in the directory whose name (or the
 * part of their email before "@") follows an "@" in the text. Sent with the
 * comment as `mentions`; the backend alerts them.
 */
export const mentionsIn = (text) => {
  const t = String(text || "").toLowerCase();
  if (!t.includes("@")) return [];
  return Object.values(PEOPLE)
    .filter((p) => {
      const handle = p.key.split("@")[0];
      return (
        (p.name && p.name !== p.key && t.includes(`@${p.name.toLowerCase()}`)) ||
        t.includes(`@${handle}`)
      );
    })
    .map((p) => p.key);
};

export const COL = {
  // One Requests column: an Urgent section pinned above a ranked Queue.
  REQUESTS: "requests",
  BACKEND: "needs_backend",
  PROGRESS: "in_progress",
  REVIEW: "awaiting_review",
  DONE: "completed",
};

export const COLUMNS = [
  {
    key: COL.REQUESTS,
    label: "Requests",
    dot: steel.main,
    // The order is a priority signal for development, not a promised build order.
    blurb: "One ranked list — top gets worked first",
    ranked: true,
    // Wide enough for the quick-add footer (company · Full form · Cancel ·
    // Add to bottom ▾) on one line.
    minWidth: 440,
    zone: "requests",
  },
  // Backend first: a card waiting on the backend sits before the frontend
  // work it unblocks.
  {
    key: COL.BACKEND,
    label: "Requires Backend Changes",
    dot: status.warning.fg,
    blurb: "New endpoint or data-model change",
    zone: "development",
  },
  {
    key: COL.PROGRESS,
    label: "Frontend In Progress",
    dot: teal[500],
    blurb: "Being built now",
    zone: "development",
  },
  {
    key: COL.REVIEW,
    label: "Completed - Awaiting Review",
    dot: status.missing.solid,
    blurb: "Approve, or send back with a comment",
    zone: "review",
  },
  {
    key: COL.DONE,
    label: "Approved",
    dot: status.success.solid,
    blurb: "Approved by the requester",
    zone: "review",
  },
];

export const columnByKey = (key) => COLUMNS.find((c) => c.key === key);

/** The lane new cards are filed into. */
export const REQUEST_COLS = [COL.REQUESTS];

/**
 * What a request needs — any combination, or none ("not tagged yet"; the
 * developer tags it on pickup). Fix / Change / Question replaced the single
 * Feature / Bug / Question type.
 */
export const NEEDS = [
  { key: "fix", label: "Fix", bg: status.error.bg, fg: status.error.fg },
  { key: "change", label: "Change", bg: steel.tint, fg: steel.main },
  { key: "question", label: "Question", bg: amber.light, fg: amber.chipText },
];

export const needByKey = (key) => NEEDS.find((n) => n.key === key);

/** Canonical order (fix, change, question), de-duplicated, unknown keys dropped. */
export const cleanNeeds = (needs) => NEEDS.map((n) => n.key).filter((k) => (needs || []).includes(k));

/**
 * How soon a request is needed. Urgent cards are pinned above the Queue and
 * worked first; No rush is a label only — it doesn't change the order.
 */
export const URGENCY = [
  { key: "urgent", label: "Urgent" },
  { key: "normal", label: "Normal" },
  { key: "no_rush", label: "No rush" },
];

/** Sort for the Requests column: Urgent first, then rank. */
export const requestsOrder = (a, b) =>
  Number(b.urgency === "urgent") - Number(a.urgency === "urgent") || a.rank - b.rank;

/**
 * The three groups the five lanes sit in. The development zone is drawn as a
 * tinted panel.
 */
export const ZONES = [
  { key: "requests", label: "Requests" },
  {
    key: "development",
    label: "Development",
    tinted: true,
  },
  { key: "review", label: "Review" },
];

/** Which company (LOS) an item is for. New items default to both. */
export const PLATFORMS = [
  { value: "both", label: "Both", short: "EV + LQ" },
  { value: "ev", label: "Eastview", short: "EV" },
  { value: "lq", label: "Lendmarq", short: "LQ" },
];

/**
 * The loan drilldown's tabs, as `{ path, label }` — the path is the URL segment
 * after `/loan/{id}/`. Mirrors the tab list in LoanDrilldownScreen's
 * NavigationBar (that list is built per user inside the component, so it can't
 * be imported); a pasted link to a tab not listed here still works and shows
 * its path.
 */
const DRILLDOWN_TABS = [
  { path: "summary", label: "Summary" },
  { path: "loan-information", label: "Loan Info" },
  { path: "documents", label: "Documents" },
  { path: "closed-package", label: "Closed Package", only: "acquisition" },
  { path: "economics", label: "Trade Economics", only: "acquisition" },
  { path: "entity", label: "Entity" },
  { path: "applicants", label: "Applicants" },
  { path: "properties", label: "Properties" },
  { path: "contacts", label: "Contacts" },
  { path: "orders", label: "Orders" },
  { path: "terms", label: "Terms" },
  { path: "fees", label: "Fees" },
  { path: "budget-draws", label: "Budget & Draws" },
  { path: "loan-lock", label: "Loan Lock" },
  { path: "liquidity-calculations", label: "Liquidity Calc." },
  { path: "experience", label: "Experience Calc." },
  { path: "dscr-calculations", label: "DSCR Calc." },
  { path: "chat", label: "Chat" },
  { path: "conditions", label: "Conditions" },
  { path: "exceptions", label: "Exceptions" },
  { path: "sizer", label: "Sizer" },
  { path: "appraisals", label: "Appraisals" },
  { path: "closing", label: "Closing" },
  { path: "wire-and-funding", label: "Wire & Funding" },
  { path: "checklist", label: "Checklist" },
  { path: "ai-review", label: "AI Review" },
  { path: "admin", label: "Admin" },
];

/** The tabs alphabetically — the order the Drilldown tab picker lists them. */
export const LOAN_AREAS = [...DRILLDOWN_TABS].sort((x, y) => x.label.localeCompare(y.label));

/**
 * Top-level screens the "Loan or screen" box can find by name (the header nav).
 * A pasted link to any other page still works; it's labelled from its path.
 */
export const APP_SCREENS = [
  { path: "/loans/pre-close", label: "Pre-Close Pipeline" },
  { path: "/loans/closing", label: "Clear to Close" },
  { path: "/loans/closing/calendar", label: "Closing Calendar" },
  { path: "/loans/closed", label: "Closed Loans" },
  { path: "/acquisitions", label: "Acquisitions Pipeline" },
  { path: "/loans/full-pipeline", label: "Full Pipeline" },
  { path: "/correspondents", label: "Correspondents" },
  { path: "/lock-queue", label: "Lock Queue" },
  { path: "/fundings", label: "Fundings" },
  { path: "/appraisal-queue", label: "Appraisal Queue" },
  { path: "/users", label: "Users" },
  { path: "/support", label: "Support" },
  { path: "/changelog", label: "Changelog" },
  { path: "/admin/guidelines", label: "Admin › Guidelines" },
  { path: "/admin/pricing", label: "Admin › Pricing" },
  { path: "/sizers", label: "Sizers" },
  { path: "/admin/market-data", label: "Admin › Market Data" },
  { path: "/admin/banned-appraisers", label: "Admin › Banned Appraisers" },
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * The drilldown tabs that exist for a kind of loan: Closed Package and Trade
 * Economics are acquisition-only, and the Closing tab reads "Settlement" on an
 * acquisition (it schedules a trade settlement).
 */
export const tabsFor = (loanType) =>
  LOAN_AREAS.filter((t) => !t.only || t.only === loanType).map((t) =>
    t.path === "closing" ? { ...t, label: loanType === "acquisition" ? "Settlement" : "Closing" } : t,
  );

/** The card's "Screen › Tab" line for a linked loan or screen. */
export const contextArea = (ctx) => {
  if (!ctx) return "";
  if (ctx.kind === "screen") return ctx.info?.label || ctx.screen;
  if (ctx.kind === "text") return ctx.text.trim();
  const tab = tabsFor(ctx.loanType).find((t) => t.path === ctx.tab)?.label;
  return tab ? `Loan Drilldown › ${tab}` : "Loan Drilldown";
};

/** Same link? (kind + ids + tab — never the display text). */
export const sameContext = (a, b) =>
  (a?.kind || null) === (b?.kind || null) &&
  (a?.loanId || null) === (b?.loanId || null) &&
  (a?.tab || null) === (b?.tab || null) &&
  (a?.screen || null) === (b?.screen || null) &&
  (a?.text || null) === (b?.text || null);

export const platformShort = (v) =>
  (PLATFORMS.find((p) => p.value === v) || PLATFORMS[0]).short;


/**
 * No roles: everyone who can open the board (super admins, decided by the
 * frontend) may move any card to any column, edit and delete anything.
 */
export const canMove = () => true;

/** Every other column — the Stage dropdown's options. */
export const movesFrom = (from) => COLUMNS.map((c) => c.key).filter((k) => k !== from);

/** New cards are filed into the Requests column. */
export const canAddTo = (colKey) => colKey === COL.REQUESTS;

/** Label for the button that performs a move — the verb, not the column. */
export const moveLabel = (from, to) => {
  if (from === COL.REVIEW && to === COL.DONE) return "Approve";
  if (from === COL.REVIEW && to === COL.PROGRESS) return "Send back";
  if (to === COL.PROGRESS) return "Start work";
  if (to === COL.REVIEW) return "Submit for review";
  if (to === COL.BACKEND) return "Requires backend changes";
  if (to === COL.REQUESTS && from === COL.BACKEND) return "Return to requests";
  return `Move to ${columnByKey(to)?.label}`;
};

/** Rank between two neighbours — one write per drag, no renumbering. */
export const rankBetween = (before, after) => {
  if (before == null && after == null) return 1000;
  if (before == null) return after - 1000;
  if (after == null) return before + 1000;
  return (before + after) / 2;
};

/**
 * Cards filed from a Claude Code session (the /request-board-* commands) are
 * created with the user's own login, so `createdBy` is the person, and carry
 * this prefix in their title to say they were auto-created. The board shows an
 * "Auto" label and hides the prefix; edits keep it.
 */
export const AUTO_PREFIX = "(Auto) ";
export const isAuto = (title) => String(title || "").startsWith(AUTO_PREFIX);
export const displayTitle = (title) =>
  isAuto(title) ? String(title).slice(AUTO_PREFIX.length) : title || "";

/**
 * Recurring Encounters counts (update request 2026-10-20). The card carries
 * a summary the backend keeps current: `hitCount`, `lastHitAt`, `lastHitBy`
 * and `hitsByPerson` [{ by, count, lastAt }]. Anyone can count a card that
 * isn't Approved yet, as many times as they run into it.
 */
/**
 * The header button that moves a whole lane on (update request 2026-10-20):
 * Frontend In Progress → Complete All, Completed - Awaiting Review → Approve All.
 */
export const BULK_MOVES = {
  [COL.PROGRESS]: { label: "Complete All", to: COL.REVIEW, verb: "complete" },
  [COL.REVIEW]: { label: "Approve All", to: COL.DONE, verb: "approve" },
};

export const canHit = (item) => !!item && item.column !== COL.DONE;

export const hitSummary = (item, me) => {
  const people = Array.isArray(item?.hitsByPerson) ? item.hitsByPerson : [];
  const mine = people.find((p) => p.by === me);
  return {
    count: Number(item?.hitCount) || 0,
    people: [...people].sort((a, b) => b.count - a.count),
    mine: mine?.count || 0,
    lastAt: item?.lastHitAt || null,
    lastBy: item?.lastHitBy || null,
  };
};

/** The item's reference number, as the drawer header shows it. */
export const itemRef = (item) => (item?.number ? `REQ-${item.number}` : "REQ");

/** "1d", "4h", "just now" — the card's age chip. */
export const ageLabel = (iso) => {
  const t = timestampMs(iso);
  if (t == null) return "";
  const ms = Date.now() - t;
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "now";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export const NEUTRAL = { bg: slate[100], fg: slate[500] };
